// CheckInList - Show who's currently at a place

import React, { useState, useEffect } from 'react';
import { Users, Clock, MessageSquare } from 'lucide-react';
import {
  subscribeToPlaceCheckins,
  formatTimeRemaining,
  type CheckIn,
} from '../../services/checkinService';

interface CheckInListProps {
  placeId: string;
  currentUserId?: string;
}

export function CheckInList({ placeId, currentUserId }: CheckInListProps) {
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToPlaceCheckins(placeId, (loadedCheckins) => {
      setCheckins(loadedCheckins);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [placeId]);

  if (isLoading) {
    return (
      <div className="p-4 bg-gray-50 rounded-xl animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
        <div className="space-y-2">
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (checkins.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-xl text-center">
        <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500">Gerade ist niemand hier</p>
        <p className="text-xs text-gray-400 mt-1">Sei der Erste!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-gray-900">
          {checkins.length} {checkins.length === 1 ? 'Person' : 'Personen'} hier
        </h3>
      </div>

      {/* Check-in list */}
      <div className="space-y-2">
        {checkins.map((checkin) => {
          const isCurrentUser = checkin.userId === currentUserId;

          return (
            <div
              key={checkin.id}
              className={`flex items-start gap-3 p-3 rounded-xl transition ${
                isCurrentUser ? 'bg-indigo-500/5 border border-indigo-500/20' : 'bg-gray-50'
              }`}
            >
              {/* Avatar */}
              {checkin.userPhoto ? (
                <img
                  src={checkin.userPhoto}
                  alt={checkin.userName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-indigo-500/10 rounded-full flex items-center justify-center">
                  <span className="text-indigo-600 font-semibold">
                    {checkin.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${isCurrentUser ? 'text-indigo-600' : 'text-gray-900'}`}>
                    {isCurrentUser ? 'Du' : checkin.userName}
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTimeRemaining(checkin.expiresAt)}
                  </span>
                </div>
                {checkin.message && (
                  <p className="text-sm text-gray-600 mt-1 flex items-start gap-1">
                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span className="italic">"{checkin.message}"</span>
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CheckInList;
